import React, { useState } from 'react';
import { login, register } from '../services/auth';

interface LoginPageProps {
    onLoginSuccess: () => void;
}

export const LoginPage: React.FC<LoginPageProps> = ({ onLoginSuccess }) => {
    const [isLogin, setIsLogin] = useState(true);
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [fullName, setFullName] = useState('');
    const [colorPreference, setColorPreference] = useState('#000000');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);
        setError('');

        try {
            if (isLogin) {
                const data = await login(email, password);
                localStorage.setItem('token', data.access_token);
                onLoginSuccess();
            } else {
                await register({
                    email,
                    password,
                    full_name: fullName,
                    color_preference: colorPreference,
                });
                // Auto login after register for better UX
                const data = await login(email, password);
                localStorage.setItem('token', data.access_token);
                onLoginSuccess();
            }
        } catch (err: any) {
            console.error(err);
            // Handle different error types
            if (err.code === 'ERR_NETWORK' || err.message === 'Network Error') {
                setError('Unable to connect to server. Please make sure the backend is running.');
            } else if (err.response?.data?.detail) {
                // Backend returned an error message (e.g., duplicate user, wrong credentials)
                const detail = err.response.data.detail;
                if (typeof detail === 'string') {
                    if (detail.includes('already exists')) {
                        setError('An account with this email already exists. Please sign in instead.');
                    } else {
                        setError(detail);
                    }
                } else if (Array.isArray(detail)) {
                    // Handle Pydantic validation errors
                    setError(detail.map(e => e.msg).join(', '));
                } else {
                    setError(JSON.stringify(detail));
                }
            } else if (err.response?.status === 400) {
                setError('Invalid request. Please check your input.');
            } else if (err.response?.status === 401) {
                setError('Invalid email or password.');
            } else {
                setError('Authentication failed. Please try again.');
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen flex items-center justify-center bg-surface-subtle px-4 font-sans text-main">
            <div className="max-w-md w-full space-y-8 bg-surface p-8 rounded shadow-card border border-border-base">
                <div className="text-center">
                    {/* Placeholder for Logo - will be replaced by Logo component later */}
                    <div className="mb-4 flex justify-center">
                        <div className="flex items-center gap-3">
                            <span className="h-0.5 w-6 bg-gray-400 relative">
                                <span className="absolute top-1/2 left-3 -translate-y-1/2 w-2.5 h-2.5 bg-primary rounded-full"></span>
                            </span>
                            <span className="font-medium text-xl tracking-tight text-main">MoodBoard</span>
                        </div>
                    </div>

                    <h2 className="mt-6 text-2xl font-medium tracking-tight text-main">
                        {isLogin ? 'Sign in to MoodBoard' : 'Create an Account'}
                    </h2>
                    <p className="mt-2 text-sm text-muted">
                        {isLogin ? 'Or ' : 'Already have an account? '}
                        <button
                            onClick={() => setIsLogin(!isLogin)}
                            className="font-medium text-primary hover:text-primary-hover underline decoration-1 underline-offset-2"
                        >
                            {isLogin ? 'create a new account' : 'sign in'}
                        </button>
                    </p>
                </div>
                <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
                    <div className="space-y-4">
                        {!isLogin && (
                            <>
                                <div>
                                    <label htmlFor="fullName" className="sr-only">Full Name</label>
                                    <input
                                        id="fullName"
                                        type="text"
                                        required
                                        className="appearance-none rounded relative block w-full px-3 py-2 border border-border-base placeholder-muted text-main focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary sm:text-sm bg-surface"
                                        placeholder="Full Name"
                                        value={fullName}
                                        onChange={(e) => setFullName(e.target.value)}
                                    />
                                </div>
                                <div>
                                    <label htmlFor="color" className="block text-sm font-medium text-muted mb-1">Color Preference</label>
                                    <div className="flex items-center gap-2">
                                        <input
                                            id="color"
                                            type="color"
                                            className="h-8 w-8 rounded cursor-pointer border border-border-base bg-surface"
                                            value={colorPreference}
                                            onChange={(e) => setColorPreference(e.target.value)}
                                        />
                                        <span className="text-sm text-muted">{colorPreference}</span>
                                    </div>
                                </div>
                            </>
                        )}
                        <div>
                            <label htmlFor="email-address" className="sr-only">Email address</label>
                            <input
                                id="email-address"
                                type="email"
                                required
                                className="appearance-none rounded relative block w-full px-3 py-2 border border-border-base placeholder-muted text-main focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary sm:text-sm bg-surface"
                                placeholder="Email address"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                            />
                        </div>
                        <div>
                            <label htmlFor="password" className="sr-only">Password</label>
                            <input
                                id="password"
                                type="password"
                                required
                                className="appearance-none rounded relative block w-full px-3 py-2 border border-border-base placeholder-muted text-main focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary sm:text-sm bg-surface"
                                placeholder="Password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                            />
                        </div>
                    </div>

                    {error && (
                        <div className="text-primary text-sm text-center">{error}</div>
                    )}

                    <div>
                        <button
                            type="submit"
                            disabled={loading}
                            className="group relative w-full flex justify-center py-2 px-4 border border-primary text-sm font-medium rounded text-white bg-primary hover:bg-primary-hover focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-primary disabled:opacity-50 shadow-sm active:shadow-inner transition-all"
                        >
                            {loading ? 'Processing...' : (isLogin ? 'Sign in' : 'Sign up')}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};
